/**
 * AI가 돌려준 짧은 문안을 프레젠테이션 슬라이드·활동지 쪽에 배치한다.
 * 평가지 생성과 같이, 모델은 문장만 만들고 좌표는 여기서 정한다.
 */
import type {
  ProgramSlideLayoutV1,
  ProgramSlideShapeBlockV1,
  ProgramSlideTableBlockV1,
  ProgramSlideTableCellV1,
  ProgramSlideTextBoxV1,
  ProgramSlideWebpageBlockV1,
} from '../types/programContentSlideCanvas';
import { EMPTY_SLIDE_LAYOUT } from '../types/programContentSlideCanvas';
import {
  EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
  createEmptySheetLayoutSubset,
  type ProgramSlideLayoutSubsetV1,
} from '../types/evaluationPlanSheetCanvas';
import {
  LEARNING_CONTENT_WORKSHEET_TITLE,
  createDefaultLearningContentWorksheetDocument,
  normalizeLearningContentWorksheetDocument,
  type LearningContentWorksheetDocumentV1,
} from '../types/learningContentWorksheetCanvas';
import type {
  AiLearningSlideDraft,
  AiLearningWorksheetPageDraft,
  AiWorksheetBlock,
  AiWorksheetV2,
} from './aiLearningMaterial';
import { coerceAiWorksheetBlock, coerceAiWorksheetStageTitles } from './aiLearningMaterial';
import { measureHeightPx } from './aiEvaluationBuilders';
import { sanitizeSheetCodeHtml } from './sheetCodeHtmlSanitize';

const SLIDE_W = 1280;
const SLIDE_H = 720;
const SHEET_W = 794;
const SHEET_H = 1123;

let seq = 0;
function nextId(prefix: string): string {
  seq += 1;
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}-${seq}-${rand}`;
}

function slideHeight(text: string, fontSize: number, widthNorm: number, maxNorm: number): number {
  const widthPx = Math.max(80, SLIDE_W * widthNorm);
  const perLine = Math.max(6, Math.floor(widthPx / (fontSize * 0.95)));
  const lines = String(text || '')
    .split('\n')
    .reduce((sum, line) => sum + Math.max(1, Math.ceil(Math.max(line.trim().length, 1) / perLine)), 0);
  const px = lines * fontSize * 1.35 + 8;
  return Math.min(maxNorm, Math.max(fontSize / SLIDE_H + 0.02, px / SLIDE_H));
}

function slideBox(
  text: string,
  x: number,
  y: number,
  w: number,
  h: number,
  fontSizePx: number,
  bold: boolean,
  align: ProgramSlideTextBoxV1['textAlign'],
  colorHex: string,
  zIndex: number
): ProgramSlideTextBoxV1 {
  return {
    id: nextId('ai-slide'),
    x,
    y,
    w,
    h,
    text,
    fontSizePx,
    fontFamily: 'pretendard',
    colorHex,
    bold,
    textAlign: align,
    lineHeight: 1.35,
    zIndex,
  };
}

function emptySlide(): ProgramSlideLayoutV1 {
  return {
    ...EMPTY_SLIDE_LAYOUT,
    v: 1,
    pageBackgroundColorHex: '#ffffff',
    defaultTextColorHex: '#141414',
    defaultBackgroundColorHex: '#ffffff',
    boxes: [],
    shapes: [],
  };
}

function buildTitleSlide(slide: AiLearningSlideDraft): ProgramSlideLayoutV1 {
  const layout = emptySlide();
  const title = slide.title.trim() || '오늘의 학습';
  const body = slide.body.trim();
  const titleH = slideHeight(title, 64, 0.8, 0.34);
  const titleY = body ? 0.3 : 0.38;
  layout.boxes.push(slideBox(title, 0.1, titleY, 0.8, titleH, 64, true, 'center', '#141414', 2));
  if (body) {
    const bodyH = slideHeight(body, 32, 0.76, 0.22);
    layout.boxes.push(slideBox(body, 0.12, titleY + titleH + 0.03, 0.76, bodyH, 32, false, 'center', '#3d4a45', 3));
  }
  return layout;
}

function buildStageSlide(slide: AiLearningSlideDraft): ProgramSlideLayoutV1 {
  const layout = emptySlide();
  const stage = slide.stage.trim();
  let y = 0.07;
  let z = 2;
  if (stage) {
    layout.boxes.push(slideBox(stage, 0.08, y, 0.84, 0.07, 26, true, 'left', '#0f6f62', z));
    y += 0.09;
    z += 1;
  }
  const title = slide.title.trim();
  if (title) {
    const h = slideHeight(title, 46, 0.84, 0.24);
    layout.boxes.push(slideBox(title, 0.08, y, 0.84, h, 46, true, 'left', '#141414', z));
    y += h + 0.03;
    z += 1;
  }
  const body = slide.body.trim();
  if (body && y < 0.78) {
    const h = slideHeight(body, 30, 0.84, Math.max(0.12, 0.78 - y));
    layout.boxes.push(slideBox(body, 0.08, y, 0.84, h, 30, false, 'left', '#24302c', z));
    y += h + 0.04;
    z += 1;
  }
  const question = slide.question.trim();
  if (question && y < 0.9) {
    const h = slideHeight(question, 32, 0.84, Math.max(0.1, 0.94 - y));
    layout.boxes.push(slideBox(question, 0.08, y, 0.84, h, 32, true, 'left', '#1e4b8a', z));
  }
  return layout;
}

export function buildPresentationLayoutsFromAi(slides: AiLearningSlideDraft[]): ProgramSlideLayoutV1[] {
  const layouts: ProgramSlideLayoutV1[] = [];
  for (const slide of slides) {
    if (!slide) continue;
    const layout = slide.kind === 'title' ? buildTitleSlide(slide) : buildStageSlide(slide);
    if (layout.boxes.length) layouts.push(layout);
    if (layouts.length >= 8) break;
  }
  return layouts;
}

/* ───────────────────────── 활동지 (블록 → A4 2단 격자) ───────────────────────── */

const PAGE_TOP_GAP_PX = 14;
const PAGE_BOTTOM_PX = 36;
const MARGIN_X = 0.06;
const CONTENT_W_PX = SHEET_W * (1 - MARGIN_X * 2);
const ROW_GAP_PX = 12;
const PAD_PX = 10;
/** 활동지 쪽 수: 최대 2쪽(자연스럽게 들어가면 1쪽). 3쪽은 만들지 않는다 */
export const MAX_WORKSHEET_PAGES = 2;
/** 촘촘한 단계(4)에서 읽기자료를 이 글자 수 안팎으로 줄인다(문장 끝에서 자름) */
const READING_COMPACT_CHARS = 320;
/** 가장 촘촘한 단계(5, 배움 칸 더 줄이기)의 읽기자료 글자 수 */
const READING_DEEP_CHARS = 200;
/** 가장 촘촘한 단계(6)의 읽기자료 글자 수 */
const READING_TIGHT_CHARS = 160;
const THEME_HEX = '#10504a';
const INK_HEX = '#141414';
const SUB_HEX = '#4a5250';
const LINE_HEX = '#9aa1a8';
const FRAME_HEX = '#cfd5d3';
const SOFT_FILL_HEX = '#f5f6f6';
const HEAD_FILL_HEX = '#eef0f1';
/** 1단(가로 전체) 기준 블록 높이 힌트. 서버 프롬프트의 S/M/L 값과 맞춘다 */
const SIZE_TARGET_PX: Record<AiWorksheetBlock['size'], number> = { S: 140, M: 220, L: 340 };
const HTML_MAX_H_PX = 560;
/** 편집기 표의 한 줄 행 높이(내용 높이로 자동 맞춤되므로 이보다 큰 빈 행은 유지되지 않음) */
const TABLE_ROW_MIN_PX = 30;
/** 편집기는 글자 크기를 캔버스 배율로 줄이지 않아 화면에서 줄바꿈이 더 생긴다 → 좁게 재고 조금 더한다 */
const TEXT_MEASURE_W_RATIO = 0.92;
const TEXT_EXTRA_H_PX = 4;

type SheetElements = {
  boxes: ProgramSlideTextBoxV1[];
  shapes: ProgramSlideShapeBlockV1[];
  tables: ProgramSlideTableBlockV1[];
  webpages: ProgramSlideWebpageBlockV1[];
};

/** 넘침 단계: 0 기본 → 1 쓰기 줄 줄이기 → 2 그리기 칸 줄이기 → 3 읽기자료 15→14px → 4 반 칸 짝짓기 */
/** 0 기본 → 1 쓰기 칸 줄임 → 2 그리기 칸 줄임 → 3 읽기 글자 14px → 4 촘촘하게(보기 간격·답칸·그리기·읽기 길이) */
/** 5: 정리를 줄여도 넘칠 때 배움 칸을 더 작게(읽기 200자 안팎, 답칸·그리기 칸 더 줄임) */
/** 6: 그래도 넘칠 때 배움 활동을 빼지 않고 간격·답칸을 더 촘촘하게(답칸을 문제 옆에, 순서 표 두 줄씩) */
type FitLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** 읽기자료를 문장 끝에서 잘라 줄인다(너무 짧아지면 글자 수로 자르고 …) */
function trimReadingText(text: string, max = READING_COMPACT_CHARS): string {
  if (text.length <= max) return text;
  const head = text.slice(0, max);
  const cut = Math.max(head.lastIndexOf('다.'), head.lastIndexOf('요.'), head.lastIndexOf('. '), head.lastIndexOf('?'), head.lastIndexOf('!'), head.lastIndexOf('\n'));
  if (cut >= max * 0.5) return `${head.slice(0, cut + (head.startsWith('다.', cut) || head.startsWith('요.', cut) ? 2 : 1)).trim()} …`;
  return `${head.trim()}…`;
}

type Compiled = {
  /** 카드 전체 자연 높이(px, 테두리·안쪽 여백 포함) */
  h: number;
  draw: (out: SheetElements, x: number, y: number, w: number, h: number, z: () => number) => void;
};

const nx = (px: number) => px / SHEET_W;
const ny = (px: number) => px / SHEET_H;

function textEl(
  x: number,
  y: number,
  w: number,
  h: number,
  text: string,
  fontSizePx: number,
  opts: { bold?: boolean; color?: string; align?: ProgramSlideTextBoxV1['textAlign']; lineHeight?: number; groupId: string; z: number }
): ProgramSlideTextBoxV1 {
  return {
    id: nextId('ai-ws'),
    x: nx(x),
    y: ny(y),
    w: nx(w),
    h: ny(h),
    text,
    fontSizePx,
    fontFamily: 'pretendard',
    colorHex: opts.color ?? INK_HEX,
    bold: opts.bold ?? false,
    textAlign: opts.align ?? 'left',
    lineHeight: opts.lineHeight ?? 1.45,
    zIndex: opts.z,
    groupId: opts.groupId,
  };
}

function shapeEl(
  x: number,
  y: number,
  w: number,
  h: number,
  opts: {
    shape?: ProgramSlideShapeBlockV1['shape'];
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    radius?: number;
    text?: string;
    fontSizePx?: number;
    color?: string;
    bold?: boolean;
    background?: 'lines' | 'grid' | 'dots';
    spacing?: number;
    groupId: string;
    z: number;
  }
): ProgramSlideShapeBlockV1 {
  const shape = opts.shape ?? 'rect';
  const el: ProgramSlideShapeBlockV1 = {
    id: nextId('ai-ws'),
    x: nx(x),
    y: ny(y),
    w: nx(w),
    h: ny(h),
    shape,
    fillColorHex: opts.fill ?? '',
    strokeColorHex: opts.stroke ?? LINE_HEX,
    strokeWidthPx: opts.strokeWidth ?? 1,
    cornerRadiusPx: opts.radius ?? 8,
    text: opts.text ?? '',
    fontSizePx: opts.fontSizePx ?? 14,
    fontFamily: 'pretendard',
    colorHex: opts.color ?? INK_HEX,
    bold: opts.bold ?? false,
    textAlign: 'center',
    zIndex: opts.z,
    groupId: opts.groupId,
  };
  if (shape === 'ruled') {
    el.ruledLineSpacingPx = opts.spacing ?? 30;
    el.ruledLineColorHex = '#c8c8c8';
    el.ruledLineDashed = false;
    if (opts.background && opts.background !== 'lines') el.ruledBackground = opts.background;
  }
  return el;
}

type TableSpec = {
  rows: string[][];
  header: boolean;
  colFracs?: number[];
  /** 행별 최소 높이(px). 없으면 32 */
  minRowH?: number[];
  align?: Array<'left' | 'center' | 'right'>;
  fontSizePx?: number;
};

function tableFracs(spec: TableSpec): number[] {
  const colCount = Math.max(1, ...spec.rows.map((r) => r.length));
  return spec.colFracs?.length === colCount ? spec.colFracs : Array.from({ length: colCount }, () => 1 / colCount);
}

function measureTable(spec: TableSpec, widthPx: number): number[] {
  const fracs = tableFracs(spec);
  const fs = spec.fontSizePx ?? 14;
  return spec.rows.map((row, ri) =>
    Math.max(
      spec.minRowH?.[ri] ?? TABLE_ROW_MIN_PX,
      ...row.map((cell, ci) =>
        cell.trim()
          ? measureHeightPx({ text: cell, widthPx: Math.max(24, widthPx * (fracs[ci] ?? 1) * TEXT_MEASURE_W_RATIO), fontSizePx: fs, bold: spec.header && ri === 0 }) - 2
          : 0
      )
    )
  );
}

/** 글상자 높이(measureHeightPx가 글상자 안쪽 여백까지 포함). 화면 배율 차이만큼 여유를 둔다 */
function measureTextBoxPx(text: string, widthPx: number, fontSizePx: number, opts: { bold?: boolean; lineHeight?: number } = {}): number {
  return Math.ceil(
    measureHeightPx({ text, widthPx: Math.max(40, widthPx * TEXT_MEASURE_W_RATIO), fontSizePx, bold: opts.bold, lineHeight: opts.lineHeight }) + TEXT_EXTRA_H_PX
  );
}

function tableEl(x: number, y: number, w: number, spec: TableSpec, rowHs: number[], groupId: string, z: number): ProgramSlideTableBlockV1 {
  const fracs = tableFracs(spec);
  const colCount = fracs.length;
  const total = rowHs.reduce((a, c) => a + c, 0) || 1;
  const cells: ProgramSlideTableCellV1[] = [];
  spec.rows.forEach((row, ri) => {
    for (let ci = 0; ci < colCount; ci += 1) {
      const head = spec.header && ri === 0;
      cells.push({
        id: nextId('ai-ws-cell'),
        text: row[ci] ?? '',
        textAlign: head ? 'center' : spec.align?.[ci] ?? 'left',
        verticalAlign: 'middle',
        colorHex: INK_HEX,
        backgroundColorHex: head ? HEAD_FILL_HEX : '#ffffff',
        bold: head,
        rowSpan: 1,
        colSpan: 1,
      });
    }
  });
  return {
    id: nextId('ai-ws-table'),
    x: nx(x),
    y: ny(y),
    w: nx(w),
    h: ny(total),
    rowCount: spec.rows.length,
    colCount,
    cells,
    rowHeightsFrac: rowHs.map((h) => h / total),
    colWidthsFrac: fracs,
    borderColorHex: '#5f6664',
    borderWidthPx: 1,
    defaultFontSizePx: spec.fontSizePx ?? 14,
    defaultFontFamily: 'pretendard',
    headerRow: spec.header,
    zIndex: z,
    groupId,
  };
}

function circled(n: number): string {
  const list = '①②③④⑤⑥⑦⑧⑨⑩';
  return list[n] ?? `${n + 1}.`;
}

/** 단계 칩: 글자 폭을 재서 한 줄로 들어가게(편집기 도형 글상자 좌우 여백 10px×2 + 테두리 + 여유) */
const CHIP_FONT_PX = 12;
const CHIP_H_PX = 30;
let chipMeasureCtx: CanvasRenderingContext2D | null | undefined;
function textWidthPx(text: string, fontPx: number, bold: boolean, tight = false): number {
  const estimate = Array.from(text).reduce(
    (a, ch) => a + (/[\u1100-\u11ff\u3130-\u318f\uac00-\ud7a3]/.test(ch) ? fontPx : fontPx * 0.62),
    0
  );
  if (chipMeasureCtx === undefined) {
    try {
      chipMeasureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
    } catch {
      chipMeasureCtx = null;
    }
  }
  if (!chipMeasureCtx) return estimate;
  chipMeasureCtx.font = `${bold ? 700 : 400} ${fontPx}px Pretendard, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif`;
  const measured = chipMeasureCtx.measureText(text).width;
  // 칩은 넘치면 안 되므로 넉넉한 쪽(추정치)을, 앞머리 간격처럼 딱 맞아야 하는 곳은 실제 잰 값을 쓴다
  return tight ? Math.max(measured, estimate * 0.85) : Math.max(estimate, measured);
}
function chipWidthPx(stage: string): number {
  return Math.ceil(Math.max(56, textWidthPx(stage, CHIP_FONT_PX, true) + 20 + 2 + 14));
}

type Part = {
  h: number;
  stretch?: boolean;
  draw: (out: SheetElements, x: number, y: number, h: number, z: () => number) => void;
};

/** 문제 기호(■)·퀴즈 제목을 붙이는 유형: ox·short는 항목마다, 나머지는 블록 하나가 한 문제 */
const QUESTION_PER_ITEM: ReadonlySet<AiWorksheetBlock['type']> = new Set<AiWorksheetBlock['type']>(['ox', 'short']);
const QUESTION_PER_BLOCK: ReadonlySet<AiWorksheetBlock['type']> = new Set<AiWorksheetBlock['type']>([
  'choice',
  'cloze',
  'matching',
  'ordering',
  'classify',
]);
function questionCount(block: AiWorksheetBlock): number {
  if (QUESTION_PER_ITEM.has(block.type)) return block.items.length;
  return QUESTION_PER_BLOCK.has(block.type) ? 1 : 0;
}

/** 일반 문제 앞 기호(번호 대신). 글자 크기의 약 0.6배, 테마 초록 */
const MARKER = '■';
const MARKER_SCALE = 0.6;
/** 정리 단계 개념 확인 퀴즈 문항 제목. 2~3문항이면 뒤에 번호(띄어쓰기 없이) */
const QUIZ_TITLE = '개념확인 퀴즈';
type QuizNo = { start: number; total: number };

type LeadLine = { h: number; draw: (out: SheetElements, x: number, y: number, groupId: string, z: () => number) => void };
/** 앞머리(■ 기호 또는 '개념확인 퀴즈N') + 글. 앞머리는 첫 줄 가운데에 맞추고, 글은 그 오른쪽에서 줄바꿈한다 */
function leadLine(lead: 'marker' | string, text: string, width: number, fs: number, bold: boolean): LeadLine {
  const isMarker = lead === 'marker';
  const leadText = isMarker ? MARKER : lead;
  const leadFs = isMarker ? Math.max(8, Math.round(fs * MARKER_SCALE)) : fs;
  // 편집기 화면에서는 글자가 잰 값보다 조금 넓게 그려지므로 여유를 둔다
  const leadW = Math.ceil(textWidthPx(leadText, leadFs, true, true) * (isMarker ? 1 : 1.08) + (isMarker ? 8 : 14));
  const textW = Math.max(40, width - leadW);
  const textH = text ? measureTextBoxPx(text, textW, fs, { bold }) : 0;
  const lineH = measureTextBoxPx('가', 400, fs, { bold });
  const leadH = measureTextBoxPx(leadText, leadW + 40, leadFs, { bold: true });
  const leadY = Math.max(0, (lineH - leadH) / 2);
  const h = Math.max(textH, leadY + leadH);
  return {
    h,
    draw: (out, x, y, groupId, z) => {
      out.boxes.push(textEl(x, y + leadY, leadW + 24, leadH, leadText, leadFs, { bold: true, color: THEME_HEX, groupId, z: z() }));
      if (text) out.boxes.push(textEl(x + leadW, y, textW, textH, text, fs, { bold, groupId, z: z() }));
    },
  };
}

/** 블록 하나 → 지시문 + 본문. 단계 이름표는 머리줄에서 한 번만 그린다. quiz는 개념 확인 퀴즈 블록일 때 문항 번호 정보 */
function compileBlock(block: AiWorksheetBlock, widthPx: number, level: FitLevel, groupId: string, quiz: QuizNo | null = null): Compiled {
  const cw = widthPx - PAD_PX * 2;
  const isQuiz = quiz !== null && block.formative === true;
  const quizLabel = (offset: number) => (quiz && quiz.total >= 2 ? `${QUIZ_TITLE}${quiz.start + offset}` : QUIZ_TITLE);
  const titleW = cw;
  const rawTitle = block.title.trim();
  // 한 문제짜리 블록: 제목 앞에 ■(일반) 또는 '개념확인 퀴즈N'(퀴즈). 항목마다 문제인 퀴즈 블록은 제목을 문항마다 붙인다
  const perItemQuiz = isQuiz && QUESTION_PER_ITEM.has(block.type);
  const titleLead = QUESTION_PER_BLOCK.has(block.type) ? (isQuiz ? quizLabel(0) : rawTitle ? 'marker' : null) : null;
  const leadTitle = titleLead ? leadLine(titleLead, rawTitle, titleW, 15, true) : null;
  const titleText = perItemQuiz ? '' : rawTitle;
  const titleH = leadTitle ? leadTitle.h : titleText ? measureTextBoxPx(titleText, titleW, 15, { bold: true }) : 0;
  const headH = titleH;
  const leadPart = (lead: 'marker' | string, text: string, fs: number, bold: boolean): Part => {
    const line = leadLine(lead, text, cw, fs, bold);
    return { h: line.h, draw: (out, x, y, _h, z) => line.draw(out, x, y, groupId, z) };
  };
  const spacer = (h: number): Part => ({ h, draw: () => undefined });

  const parts: Part[] = [];
  const shrinkWrite = level >= 1;
  const shrinkDraw = level >= 2;
  const readingFs = level >= 3 ? 14 : 15;
  const compact = level >= 4;
  const deep = level >= 5;
  const tight = level >= 6;
  const padY = tight ? 4 : PAD_PX;
  const rowMin = tight ? 26 : TABLE_ROW_MIN_PX;

  const textPart = (text: string, fs: number, opts: { bold?: boolean; color?: string; lineHeight?: number } = {}): Part => {
    const lh = opts.lineHeight ?? 1.45;
    const h = measureTextBoxPx(text, cw, fs, { bold: opts.bold, lineHeight: lh });
    return {
      h,
      draw: (out, x, y, _h, z) => out.boxes.push(textEl(x, y, cw, h, text, fs, { ...opts, lineHeight: lh, groupId, z: z() })),
    };
  };
  const tablePart = (spec: TableSpec, stretchable: boolean): Part => {
    const rowHs = measureTable(spec, cw);
    const h = rowHs.reduce((a, c) => a + c, 0);
    return {
      h,
      stretch: stretchable,
      draw: (out, x, y, hh, z) => {
        const extra = Math.max(0, hh - h);
        let hs = rowHs;
        const bodyStart = spec.header ? 1 : 0;
        const bodyCount = rowHs.length - bodyStart;
        if (extra > 0 && bodyCount > 0) hs = rowHs.map((v, i) => (i >= bodyStart ? v + extra / bodyCount : v));
        out.tables.push(tableEl(x, y, cw, spec, hs, groupId, z()));
      },
    };
  };
  /** 머리행 표 + 그 아래 칸별 빈 상자. 편집기 표는 내용 높이로 줄어들어 큰 빈 칸이 유지되지 않으므로 도형으로 만든다 */
  const blankGridPart = (heads: string[], bodyH: number): Part => {
    const spec: TableSpec = { rows: [heads], header: true };
    const headHs = measureTable(spec, cw);
    const headTotal = headHs.reduce((a, c) => a + c, 0);
    return {
      h: headTotal + bodyH,
      stretch: true,
      draw: (out, x, y, hh, z) => {
        out.tables.push(tableEl(x, y, cw, spec, headHs, groupId, z()));
        const boxH = Math.max(20, hh - headTotal);
        let cx = x;
        for (const f of tableFracs(spec)) {
          const w = cw * f;
          out.shapes.push(shapeEl(cx, y + headTotal, w, boxH, { stroke: '#5f6664', radius: 0, groupId, z: z() }));
          cx += w;
        }
      },
    };
  };
  const wordBankPart = (words: string[], label: string): Part => {
    const text = `${label}  ${words.join('   ·   ')}`;
    const h = measureTextBoxPx(text, cw - 8, 14);
    return {
      h,
      draw: (out, x, y, _h, z) =>
        out.shapes.push(shapeEl(x, y, cw, h, { fill: SOFT_FILL_HEX, stroke: FRAME_HEX, radius: 8, text, fontSizePx: 14, groupId, z: z() })),
    };
  };
  const writePart = (hPx: number, background: 'lines' | 'grid' | 'dots' | 'blank', stretchable = true): Part => ({
    h: hPx,
    stretch: stretchable,
    draw: (out, x, y, hh, z) => {
      if (background === 'blank') {
        out.shapes.push(shapeEl(x, y, cw, hh, { stroke: LINE_HEX, radius: 8, groupId, z: z() }));
        return;
      }
      out.shapes.push(
        shapeEl(x, y, cw, hh, { shape: 'ruled', stroke: LINE_HEX, radius: 8, background, spacing: background === 'lines' ? 30 : 20, groupId, z: z() })
      );
    },
  });
  /** 카드 목표 높이에서 헤더·여백을 뺀 본문 높이 */
  const bodyTarget = (factor = 1) => Math.max(60, Math.round(SIZE_TARGET_PX[block.size] * factor) - headH - PAD_PX * 2 - 4);

  switch (block.type) {
    case 'reading': {
      const text = deep
        ? trimReadingText(block.text.trim(), tight ? READING_TIGHT_CHARS : READING_DEEP_CHARS)
        : compact
          ? trimReadingText(block.text.trim())
          : block.text.trim();
      const readingLh = deep ? 1.45 : compact ? 1.5 : 1.65;
      const h = measureTextBoxPx(text, cw - 12, readingFs, { lineHeight: readingLh }) + 8;
      parts.push({
        h,
        draw: (out, x, y, _h, z) => {
          out.shapes.push(shapeEl(x, y, cw, h, { fill: SOFT_FILL_HEX, stroke: FRAME_HEX, radius: 8, groupId, z: z() }));
          out.boxes.push(textEl(x + 6, y + 4, cw - 12, h - 8, text, readingFs, { lineHeight: readingLh, groupId, z: z() }));
        },
      });
      break;
    }
    case 'short': {
      const boxH = deep ? 26 : compact ? 28 : shrinkWrite ? 30 : 36;
      block.items.forEach((qText, i) => {
        if (perItemQuiz) {
          if (i > 0) parts.push(spacer(deep ? 2 : compact ? 4 : 8));
          parts.push(leadPart(quizLabel(i), rawTitle, 15, true));
          parts.push(textPart(qText, 14));
        } else if (tight) {
          // 답칸을 문제 옆에 둔다
          const qW = Math.round(cw * 0.56);
          const line = leadLine('marker', qText, qW, 14, false);
          const rowH = Math.max(line.h, boxH);
          parts.push({
            h: rowH,
            draw: (out, x, y, _h, z) => {
              line.draw(out, x, y + Math.max(0, (rowH - line.h) / 2), groupId, z);
              out.shapes.push(shapeEl(x + qW + 8, y, cw - qW - 8, rowH, { stroke: LINE_HEX, radius: 8, groupId, z: z() }));
            },
          });
          return;
        } else {
          parts.push(leadPart('marker', qText, 14, false));
        }
        parts.push(writePart(boxH, 'blank', false));
      });
      break;
    }
    case 'long': {
      if (block.text.trim()) parts.push(textPart(block.text.trim(), 13, { color: SUB_HEX }));
      const base = block.lines > 0 ? block.lines : block.size === 'S' ? 3 : block.size === 'L' ? 8 : 5;
      const lines = deep ? Math.max(2, base - 4) : compact ? Math.max(2, base - 3) : shrinkWrite ? Math.max(2, base - 2) : base;
      parts.push(writePart(lines * 30 + 10, 'lines'));
      break;
    }
    case 'cloze': {
      if (block.wordBank.length) parts.push(wordBankPart(block.wordBank, '[낱말 상자]'));
      parts.push(textPart(block.text.replace(/_{3,}/g, '(              )'), 15, { lineHeight: tight ? 1.6 : 2 }));
      break;
    }
    case 'choice': {
      if (block.text.trim()) parts.push(textPart(block.text.trim(), 14));
      // 보기가 짧으면 한 줄에 나란히, 길면 한 줄에 하나씩
      const options = block.items.map((item, i) => `${circled(i)} ${item}`);
      const inline = options.join('       ');
      parts.push(
        textWidthPx(inline, 14, false) <= cw * (deep ? 1 : compact ? 0.95 : 0.85)
          ? textPart(inline, 14)
          : textPart(options.join('\n'), 14, { lineHeight: deep ? 1.35 : compact ? 1.45 : 1.7 })
      );
      break;
    }
    case 'ox': {
      if (perItemQuiz) {
        block.items.forEach((item, i) => {
          if (i > 0) parts.push(spacer(compact ? 4 : 8));
          parts.push(leadPart(quizLabel(i), rawTitle, 15, true));
          parts.push(
            tablePart(
              { rows: [[item, '○      ×']], header: false, colFracs: [0.8, 0.2], align: ['left', 'center'], minRowH: [TABLE_ROW_MIN_PX] },
              false
            )
          );
        });
        break;
      }
      parts.push(
        tablePart(
          {
            rows: [['문장', '○ / ×'], ...block.items.map((item, i) => [`${MARKER} ${item}`, '○      ×'])],
            header: true,
            colFracs: [0.8, 0.2],
            align: ['left', 'center'],
            minRowH: [rowMin, ...block.items.map(() => rowMin)],
          },
          false
        )
      );
      break;
    }
    case 'matching': {
      const boxW = cw * 0.38;
      const rows = block.pairs.map((pair) => ({
        ...pair,
        h: Math.max(
          tight ? 24 : deep ? 26 : compact ? 28 : 34,
          measureTextBoxPx(pair.left, boxW, 14) - (tight ? 6 : 0),
          measureTextBoxPx(pair.right, boxW, 14) - (tight ? 6 : 0)
        ),
      }));
      const gap = tight ? 2 : deep ? 4 : compact ? 5 : 8;
      const total = rows.reduce((a, r) => a + r.h, 0) + gap * Math.max(0, rows.length - 1);
      parts.push({
        h: total,
        draw: (out, x, y, _h, z) => {
          let yy = y;
          for (const row of rows) {
            out.shapes.push(shapeEl(x, yy, boxW, row.h, { stroke: LINE_HEX, radius: 6, text: row.left, fontSizePx: 14, groupId, z: z() }));
            out.shapes.push(
              shapeEl(x + boxW + 8, yy + row.h / 2 - 4, 8, 8, { shape: 'circle', fill: '#333333', stroke: '#333333', radius: 0, groupId, z: z() })
            );
            out.shapes.push(
              shapeEl(x + cw - boxW - 16, yy + row.h / 2 - 4, 8, 8, { shape: 'circle', fill: '#333333', stroke: '#333333', radius: 0, groupId, z: z() })
            );
            out.shapes.push(
              shapeEl(x + cw - boxW, yy, boxW, row.h, { stroke: LINE_HEX, radius: 6, text: row.right, fontSizePx: 14, groupId, z: z() })
            );
            yy += row.h + gap;
          }
        },
      });
      break;
    }
    case 'ordering': {
      if (tight && block.items.length >= 4) {
        const half = Math.ceil(block.items.length / 2);
        const bodyRows = Array.from({ length: half }, (_, i) => {
          const b = block.items[i + half];
          return ['(     )', block.items[i], b !== undefined ? '(     )' : '', b ?? ''];
        });
        parts.push(
          tablePart(
            {
              rows: [['순서', '내용', '순서', '내용'], ...bodyRows],
              header: true,
              colFracs: [0.12, 0.38, 0.12, 0.38],
              align: ['center', 'left', 'center', 'left'],
              minRowH: [rowMin, ...bodyRows.map(() => rowMin)],
            },
            false
          )
        );
        break;
      }
      parts.push(
        tablePart(
          {
            rows: [['순서', '내용'], ...block.items.map((item) => ['(     )', item])],
            header: true,
            colFracs: [0.2, 0.8],
            align: ['center', 'left'],
            minRowH: [rowMin, ...block.items.map(() => rowMin)],
          },
          false
        )
      );
      break;
    }
    case 'classify': {
      parts.push(wordBankPart(block.items, '[분류할 것]'));
      const perCat = Math.ceil(block.items.length / Math.max(1, block.categories.length));
      const bodyH = tight ? Math.max(40, perCat * 18 + 8) : deep ? Math.max(44, perCat * 20 + 10) : Math.max(shrinkWrite ? 56 : 70, perCat * (shrinkWrite ? 24 : 30) + 16);
      parts.push(blankGridPart(block.categories, bodyH));
      break;
    }
    case 'tableFill': {
      if (block.text.trim()) parts.push(textPart(block.text.trim(), 14));
      const rowH = rowMin;
      const hasRowLabels = block.rows.length > 0;
      const header = hasRowLabels ? ['', ...block.categories] : block.categories;
      const bodyRows = hasRowLabels
        ? block.rows.map((label) => [label, ...block.categories.map(() => '')])
        : Array.from({ length: block.size === 'L' ? 4 : 3 }, () => block.categories.map(() => ''));
      const colFracs = hasRowLabels
        ? [0.22, ...block.categories.map(() => 0.78 / Math.max(1, block.categories.length))]
        : undefined;
      parts.push(
        tablePart(
          {
            rows: [header, ...bodyRows],
            header: true,
            colFracs,
            align: hasRowLabels ? ['center'] : undefined,
            minRowH: [rowMin, ...bodyRows.map(() => rowH)],
          },
          false
        )
      );
      break;
    }
    case 'organizer': {
      const heads =
        block.variant === 'tchart'
          ? block.categories.slice(0, 2)
          : block.categories.length === 3
            ? block.categories
            : ['K 알고 있는 것', 'W 알고 싶은 것', 'L 알게 된 것'];
      const base = block.size === 'S' ? 80 : block.size === 'L' ? 240 : 150;
      const bodyH = deep ? Math.round(base * 0.45) : compact ? Math.round(base * 0.55) : shrinkWrite ? Math.round(base * 0.72) : base;
      parts.push(blankGridPart(heads, bodyH));
      break;
    }
    case 'checklist': {
      parts.push(
        tablePart(
          {
            rows: [['확인할 일', '확인'], ...block.items.map((item) => [item, '□'])],
            header: true,
            colFracs: [0.8, 0.2],
            align: ['left', 'center'],
            minRowH: [rowMin, ...block.items.map(() => rowMin)],
          },
          false
        )
      );
      break;
    }
    case 'selfAssess': {
      const narrow = widthPx < CONTENT_W_PX / 2;
      parts.push(
        tablePart(
          {
            rows: [['나를 돌아봐요', '◎ 잘함', '○ 보통', '△ 노력'], ...block.items.map((item) => [item, '', '', ''])],
            header: true,
            colFracs: narrow ? [0.49, 0.17, 0.17, 0.17] : [0.61, 0.13, 0.13, 0.13],
            minRowH: [rowMin, ...block.items.map(() => rowMin)],
            fontSizePx: narrow ? 13 : 14,
          },
          false
        )
      );
      break;
    }
    case 'drawing': {
      if (block.text.trim()) parts.push(textPart(block.text.trim(), 14));
      const before = parts.reduce((a, p) => a + p.h + 6, 0);
      const target = tight
        ? Math.max(44, bodyTarget(0.3) - before)
        : Math.max(deep ? 48 : compact ? 56 : 70, bodyTarget(deep ? 0.45 : compact ? 0.55 : shrinkDraw ? 0.72 : 1) - before);
      parts.push(writePart(target, block.variant === 'grid' ? 'grid' : block.variant === 'dots' ? 'dots' : 'blank'));
      break;
    }
    case 'html': {
      if (block.text.trim()) parts.push(textPart(block.text.trim(), 14));
      const clean = sanitizeSheetCodeHtml(block.html);
      if (clean.trim()) {
        const measured = measureHeightPx({ html: clean, widthPx: cw, fontSizePx: 16, lineHeight: 1.5 });
        const fallback = block.size === 'S' ? 110 : block.size === 'L' ? 320 : 200;
        const h = Math.min(HTML_MAX_H_PX, measured > 8 ? measured + 4 : fallback);
        parts.push({
          h,
          draw: (out, x, y, _h, z) =>
            out.webpages.push({
              id: nextId('ai-ws-code'),
              x: nx(x),
              y: ny(y),
              w: nx(cw),
              h: ny(h),
              mode: 'code',
              url: '',
              title: '',
              thumbSrc: '',
              sourceCode: clean,
              codeTransparentBackground: true,
              zIndex: z(),
              groupId,
            }),
        });
      }
      break;
    }
    default:
      break;
  }

  const PART_GAP = tight ? 3 : 6;
  const bodyH = parts.reduce((a, p) => a + p.h, 0) + Math.max(0, parts.length - 1) * PART_GAP;
  const headGap = headH && parts.length ? (tight ? 2 : 4) : 0;
  const naturalH = padY + headH + headGap + (parts.length ? bodyH : 0) + padY;

  return {
    h: Math.ceil(naturalH),
    draw: (out, x, y, _w, h, z) => {
      // 활동 전체를 감싸는 틀은 그리지 않는다(테두리·배경 없음). 안쪽 블록만 각자 모양을 가진다.
      const ix = x + PAD_PX;
      let yy = y + padY;
      if (leadTitle) {
        leadTitle.draw(out, ix, yy, groupId, z);
      } else if (titleText) {
        out.boxes.push(
          textEl(ix, yy, titleW, titleH, titleText, 15, { bold: true, groupId, z: z() })
        );
      }
      yy += headH + headGap;
      const extra = Math.max(0, h - naturalH);
      const stretchCount = parts.filter((p) => p.stretch).length;
      for (const part of parts) {
        const ph = part.h + (part.stretch && stretchCount ? extra / stretchCount : 0);
        part.draw(out, ix, yy, ph, z);
        yy += ph + PART_GAP;
      }
    },
  };
}

const STAGE_SUBTITLE_PX = 17;
const STAGE_HEAD_GAP_AFTER_PX = 2;
const DEFAULT_STAGE_TITLES: Record<AiWorksheetBlock['stage'], string> = {
  도입: '생각 열기',
  배움1: '배움 활동 1',
  배움2: '배움 활동 2',
  정리: '정리하기',
};
const STAGE_ORDER: AiWorksheetBlock['stage'][] = ['도입', '배움1', '배움2', '정리'];

/** 단계 머리줄: [단계 이름표] + 소제목 (가로 전체, 테두리·배경 없음) */
function compileStageHeader(stage: AiWorksheetBlock['stage'], subtitle: string): Compiled {
  const chipW = chipWidthPx(stage);
  const subX = chipW + 10;
  const subW = CONTENT_W_PX - PAD_PX * 2 - subX;
  const subH = subtitle ? measureTextBoxPx(subtitle, subW, STAGE_SUBTITLE_PX, { bold: true }) : 0;
  const h = Math.ceil(Math.max(CHIP_H_PX, subH) + 4);
  const groupId = `ws-stage-${STAGE_ORDER.indexOf(stage) + 1}`;
  return {
    h,
    draw: (out, x, y, _w, rowH, z) => {
      const ix = x + PAD_PX;
      out.shapes.push(
        shapeEl(ix, y + (rowH - CHIP_H_PX) / 2, chipW, CHIP_H_PX, {
          fill: THEME_HEX,
          stroke: THEME_HEX,
          radius: CHIP_H_PX / 2,
          text: stage,
          fontSizePx: CHIP_FONT_PX,
          color: '#ffffff',
          bold: true,
          groupId,
          z: z(),
        })
      );
      if (subtitle) {
        out.boxes.push(
          textEl(ix + subX, y + (rowH - subH) / 2, subW, subH, subtitle, STAGE_SUBTITLE_PX, {
            bold: true,
            color: INK_HEX,
            groupId,
            z: z(),
          })
        );
      }
    },
  };
}

/** 1단 구성: 블록마다 가로 전체 한 줄(예전 데이터의 span=1도 가로 전체로 그린다) */
function buildRows(blocks: AiWorksheetBlock[]): AiWorksheetBlock[][] {
  return blocks.map((block) => [block]);
}

type PlacedRow = {
  page: number;
  y: number;
  h: number;
  cells: Array<{ block: AiWorksheetBlock; compiled: Compiled; x: number; w: number }>;
  /** 단계 머리줄이면 있음(cells는 빈 배열). 다음 블록 줄과 같은 쪽에 붙어 다닌다 */
  header?: Compiled;
  /** 단계 머리줄이면 true */
  stageHeader?: boolean;
  /** 다음 줄과의 간격(가장 촘촘한 단계에서 줄임) */
  gap?: number;
};

function measureRow(row: AiWorksheetBlock[], level: FitLevel, quiz: { starts: Map<string, number>; total: number }): PlacedRow {
  const x0 = SHEET_W * MARGIN_X;
  const block = row[0];
  const start = quiz.starts.get(block.id);
  const compiled = compileBlock(block, CONTENT_W_PX, level, block.id, start ? { start, total: quiz.total } : null);
  return { page: 0, y: 0, h: compiled.h, cells: [{ block, compiled, x: x0, w: CONTENT_W_PX }], gap: level >= 6 ? 4 : ROW_GAP_PX };
}

function rowGapAfter(row: PlacedRow): number {
  return row.header ? STAGE_HEAD_GAP_AFTER_PX : row.gap ?? ROW_GAP_PX;
}

/** 이 줄을 놓으려면 필요한 높이: 머리줄은 이어지는 머리줄과 첫 블록 줄까지 같이(쪽 끝에 머리줄만 남지 않게) */
function rowNeedPx(rows: PlacedRow[], i: number): number {
  if (!rows[i].header) return rows[i].h;
  let need = 0;
  let k = i;
  while (k < rows.length && rows[k].header) {
    need += rows[k].h + rowGapAfter(rows[k]);
    k += 1;
  }
  return k < rows.length ? need + rows[k].h : rows[i].h;
}

/** 뒤에 블록 줄이 하나도 없는 머리줄(단계·퀴즈)을 뺀다 */
function dropOrphanHeaders(rows: PlacedRow[]): PlacedRow[] {
  const keep = rows.map(() => true);
  let hasBlockAfter = false;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].header) {
      keep[i] = hasBlockAfter;
    } else {
      hasBlockAfter = true;
    }
    // 다른 단계로 넘어가면 다시 센다: 단계 머리줄을 지나면 리셋
    if (rows[i].header && rows[i].stageHeader) hasBlockAfter = false;
  }
  return rows.filter((_, i) => keep[i]);
}

function flowRows(rows: PlacedRow[], pageCap: number, firstTopPx: number, nextTopPx: number): boolean {
  const bottom = SHEET_H - PAGE_BOTTOM_PX;
  let page = 0;
  let y = firstTopPx;
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const need = rowNeedPx(rows, i);
    if (y + need > bottom) {
      if (page + 1 >= pageCap || nextTopPx + need > bottom) return false;
      page += 1;
      y = nextTopPx;
    }
    row.page = page;
    row.y = y;
    y += row.h + rowGapAfter(row);
  }
  return true;
}

/** 놓인 순서대로 개념 확인 퀴즈 문항 번호 시작값을 정한다(일반 문제는 번호 없이 ■) */
function quizStarts(rows: AiWorksheetBlock[][]): Map<string, number> {
  const out = new Map<string, number>();
  let n = 1;
  for (const row of rows) {
    for (const block of row) {
      const count = block.formative ? questionCount(block) : 0;
      if (count > 0) {
        out.set(block.id, n);
        n += count;
      }
    }
  }
  return out;
}

/** 활동지 맨 위 제목 글자 크기 */
const WORKSHEET_TITLE_PX = 24;

export type WorksheetBuildResult = {
  document: LearningContentWorksheetDocumentV1;
  pageCount: number;
  /** 교사에게 보일 안내(뺀 블록 등). 없으면 빈 배열 */
  warnings: string[];
};

function blockLabel(block: AiWorksheetBlock): string {
  const t = block.title.trim();
  return t.length > 18 ? `${t.slice(0, 18)}…` : t || block.type;
}

/** 정리 단계의 점검표·자기평가 블록 */
function isClosingBlock(b: AiWorksheetBlock): boolean {
  return b.stage === '정리' && (b.type === 'selfAssess' || b.type === 'checklist');
}

function quizQuestionTotal(list: AiWorksheetBlock[]): number {
  return list.filter((b) => b.formative).reduce((a, b) => a + questionCount(b), 0);
}

function closingRowTotal(list: AiWorksheetBlock[]): number {
  return list.filter(isClosingBlock).reduce((a, b) => a + b.items.length, 0);
}

/**
 * 정리 줄이기: 개념확인 퀴즈를 quizMax문항까지(뒤에서부터, 최소 1문항), 점검표·자기평가를 closingMax줄까지(최소 2줄).
 * 바뀐 게 없으면 같은 배열을 돌려준다.
 */
function shrinkWrapUp(list: AiWorksheetBlock[], quizMax: number, closingMax: number): AiWorksheetBlock[] {
  let changed = false;
  const out = list.slice();
  let total = quizQuestionTotal(out);
  const limit = Math.max(1, quizMax);
  // 문항이 여럿인 퀴즈 블록(○×·단답)의 문항부터 뒤에서 줄인다
  for (let i = out.length - 1; i >= 0 && total > limit; i -= 1) {
    const b = out[i];
    if (!b.formative || !QUESTION_PER_ITEM.has(b.type)) continue;
    const keep = Math.max(1, b.items.length - (total - limit));
    if (keep < b.items.length) {
      total -= b.items.length - keep;
      out[i] = { ...b, items: b.items.slice(0, keep) };
      changed = true;
    }
  }
  // 그래도 많으면 퀴즈 블록을 뒤에서부터 뺀다(1문항은 남김)
  for (let i = out.length - 1; i >= 0 && total > limit; i -= 1) {
    const b = out[i];
    if (!b.formative) continue;
    const c = questionCount(b);
    if (total - c >= 1) {
      out.splice(i, 1);
      total -= c;
      changed = true;
    }
  }
  const rowsMax = Math.max(2, closingMax);
  for (let i = 0; i < out.length; i += 1) {
    const b = out[i];
    if (isClosingBlock(b) && b.items.length > rowsMax) {
      out[i] = { ...b, items: b.items.slice(0, rowsMax) };
      changed = true;
    }
  }
  return changed ? out : list;
}

/** 정리 줄이기 단계: 퀴즈 3→2→1문항, 점검표·자기평가 줄도 함께 줄인다 */
const WRAP_UP_STEPS: Array<{ quiz: number; closing: number }> = [
  { quiz: 2, closing: 3 },
  { quiz: 1, closing: 2 },
];

export type WorksheetBuildOptions = {
  /** 쪽 수 제한·줄이기 없이 기본 크기로 배치(저장된 활동지가 손대지 않은 AI 결과인지 비교할 때) */
  reference?: boolean;
};

/**
 * AI 활동지(v2 블록) → A4 1~2쪽(최대 2쪽, 3쪽은 만들지 않음).
 * 넘치면: 기본 줄이기(쓰기 줄·그리기 칸·읽기 글자·촘촘하게) → 정리 줄이기(퀴즈 3→2→1, 점검표·자기평가 줄)
 * → 배움 칸 더 줄이기 → 덧붙인 것(선택 블록·두 번째 읽기자료·꾸밈) 빼기. 배움 활동은 빼지 않는다.
 * 1단(블록마다 가로 전체). 1쪽은 그대로 들어갈 때만 → 2쪽에서 쓰기 줄·그리기 칸·읽기 글자·촘촘하게 순으로 줄여 맞춤 → 3쪽.
 * 3쪽에도 넘치면 선택·덧붙인 블록(두 번째 읽기자료, 꾸밈 그림 등)만 뺀다. 배움 활동·개념확인 퀴즈·자기평가는 빼지 않는다.
 * '(이어서)' 쪽은 만들지 않는다.
 */
export function buildWorksheetDocumentFromAiV2(worksheet: AiWorksheetV2, options: WorksheetBuildOptions = {}): WorksheetBuildResult {
  const base = createDefaultLearningContentWorksheetDocument();
  const headerBand = base.headerBandNorm || EVALUATION_PLAN_SHEET_HEADER_BAND_NORM;
  const topPx = SHEET_H * headerBand + PAGE_TOP_GAP_PX;
  const title = worksheet.title.trim();
  const titleH = title ? measureTextBoxPx(title, CONTENT_W_PX, WORKSHEET_TITLE_PX, { bold: true }) : 0;
  const firstTopPx = topPx + (title ? titleH + 8 : 0);
  const stageTitles = worksheet.design.stageTitles ?? {};
  const stageHeaderRow = (stage: AiWorksheetBlock['stage']): PlacedRow => {
    const header = compileStageHeader(stage, (stageTitles[stage] ?? '').trim() || DEFAULT_STAGE_TITLES[stage]);
    return { page: 0, y: 0, h: header.h, cells: [], header, stageHeader: true };
  };
  const warnings: string[] = [];

  const usedIds = new Set<string>();
  const blocks = worksheet.blocks
    .map((block, i) => {
      let id = /^[\w-]{1,24}$/.test(block.id) ? block.id : `b${i + 1}`;
      while (usedIds.has(id)) id = `${id}x`;
      usedIds.add(id);
      return { ...block, id, formative: block.formative === true };
    })
    // 단계 이름표가 단계마다 한 번만 나오도록 단계 순서로 모은다(같은 단계 안 순서는 유지)
    .map((block, index) => ({ block, index }))
    .sort((a, b) => STAGE_ORDER.indexOf(a.block.stage) - STAGE_ORDER.indexOf(b.block.stage) || a.index - b.index)
    .map((row) => row.block);

  /** 블록 줄 앞에 단계가 바뀔 때마다 단계 머리줄을 끼운다(퀴즈 제목은 각 문항 블록 안에 그린다) */
  const withStageHeaders = (blockRows: AiWorksheetBlock[][], level: FitLevel): PlacedRow[] => {
    const quiz = {
      starts: quizStarts(blockRows),
      total: blockRows.flat().filter((b) => b.formative).reduce((a, b) => a + questionCount(b), 0),
    };
    const out: PlacedRow[] = [];
    let prev: AiWorksheetBlock['stage'] | null = null;
    for (const row of blockRows) {
      const stage = row[0].stage;
      if (stage !== prev) {
        out.push(stageHeaderRow(stage));
        prev = stage;
      }
      out.push(measureRow(row, level, quiz));
    }
    return out;
  };

  const tryLayout = (list: AiWorksheetBlock[], pages: number, level: FitLevel): PlacedRow[] | null => {
    const rows = withStageHeaders(buildRows(list), level);
    return flowRows(rows, pages, firstTopPx, topPx) ? rows : null;
  };

  const levels: FitLevel[] = [0, 1, 2, 3, 4];
  // 1쪽은 그대로 들어갈 때만(1쪽 설계면 쓰기 칸만 조금 줄여 본다) → 2쪽에서 단계별로 줄여 맞춤. 3쪽은 만들지 않는다
  const attempts: Array<{ pages: number; level: FitLevel }> = options.reference
    ? [{ pages: 99, level: 0 }]
    : [
        ...(worksheet.design.pages === 1 ? ([0, 1] as FitLevel[]) : ([0] as FitLevel[])).map((level) => ({ pages: 1, level })),
        ...levels.map((level) => ({ pages: MAX_WORKSHEET_PAGES, level })),
      ];

  let placed: PlacedRow[] | null = null;
  let usedLevel: FitLevel = 0;
  for (const attempt of attempts) {
    placed = tryLayout(blocks, attempt.pages, attempt.level);
    if (placed) {
      usedLevel = attempt.level;
      break;
    }
  }
  let finalBlocks = blocks;
  const dropped: string[] = [];
  const droppedLearning: string[] = [];
  if (!placed) {
    let list = blocks;
    usedLevel = 4;
    // 1) 정리 줄이기: 개념확인 퀴즈 3→2→1문항, 점검표·자기평가 줄도 함께(배움 활동은 그대로)
    for (const step of WRAP_UP_STEPS) {
      const next = shrinkWrapUp(list, step.quiz, step.closing);
      if (next === list) continue;
      list = next;
      placed = tryLayout(list, MAX_WORKSHEET_PAGES, 4);
      if (placed) break;
    }
    // 2) 배움 칸을 더 작게(읽기자료 더 줄이기, 답칸·그리기 칸 줄이기)
    if (!placed) {
      usedLevel = 5;
      placed = tryLayout(list, MAX_WORKSHEET_PAGES, 5);
    }
    // 2-1) 그래도 넘치면 배움 활동은 그대로 두고 간격·답칸을 더 촘촘하게
    if (!placed) {
      usedLevel = 6;
      placed = tryLayout(list, MAX_WORKSHEET_PAGES, 6);
    }
    // 3) 마지막 수단: 덧붙인 것만 덜어 낸다. 도입 블록, 개념확인 퀴즈, 점검표·자기평가는 지키고 단계마다 블록 하나는 남긴다
    const isLearning = (b: AiWorksheetBlock) => b.stage === '배움1' || b.stage === '배움2';
    const isDecor = (b: AiWorksheetBlock) => b.type === 'reading' || b.type === 'html' || b.type === 'drawing';
    const introBlock = list.find((b) => b.stage === '도입');
    const firstReading = list.find((b) => b.type === 'reading');
    const isProtected = (b: AiWorksheetBlock) => b === introBlock || b.formative || isClosingBlock(b);
    const dropTiers: Array<{ test: (b: AiWorksheetBlock) => boolean; learning: boolean }> = [
      { test: (b) => b.optional && !isLearning(b), learning: false },
      { test: (b) => b.type === 'reading' && b !== firstReading, learning: false },
      { test: (b) => b.optional && isLearning(b) && isDecor(b), learning: false },
      // 예전 활동지처럼 도입 블록이 여럿이면 첫 블록은 지키고 덧붙인 것부터
      { test: (b) => b.stage === '도입', learning: false },
      { test: (b) => b.stage === '정리', learning: false },
      // 배움의 보조 자료(읽기자료·그림 자료)
      { test: (b) => isLearning(b) && (b.type === 'reading' || b.type === 'html'), learning: false },
    ];
    for (const tier of dropTiers) {
      if (placed) break;
      for (const drop of list.filter((b) => tier.test(b) && !isProtected(b)).reverse()) {
        if (list.filter((b) => b.stage === drop.stage).length <= 1) continue;
        list = list.filter((b) => b !== drop);
        (tier.learning ? droppedLearning : dropped).push(blockLabel(drop));
        placed = tryLayout(list, MAX_WORKSHEET_PAGES, 6);
        if (placed) break;
      }
    }
    finalBlocks = list;
    if (!placed) {
      // 남은 것만으로도 2쪽을 넘는 아주 드문 경우: 2쪽까지 차례로 놓고 넘친 줄은 2쪽 끝에 붙인다(3쪽은 만들지 않음)
      const rows = withStageHeaders(buildRows(list), 6);
      const bottom = SHEET_H - PAGE_BOTTOM_PX;
      let page = 0;
      let y = firstTopPx;
      for (let i = 0; i < rows.length; i += 1) {
        const need = rowNeedPx(rows, i);
        if (y + need > bottom && y > topPx && page + 1 < MAX_WORKSHEET_PAGES) {
          page += 1;
          y = topPx;
        }
        rows[i].page = page;
        rows[i].y = Math.min(y, bottom - rows[i].h);
        y += rows[i].h + rowGapAfter(rows[i]);
      }
      placed = rows;
      warnings.push('내용이 너무 많아 2쪽 아래쪽이 겹칠 수 있습니다. 편집기에서 활동을 줄여 주세요.');
    }
  }

  const pageCount = Math.max(1, ...placed.map((row) => row.page + 1));
  // 무엇을 줄였는지 알린다(정리 → 배움 칸 → 뺀 것 순)
  const notes: string[] = [];
  const quizFrom = quizQuestionTotal(blocks);
  const quizTo = quizQuestionTotal(finalBlocks);
  const closingFrom = closingRowTotal(blocks);
  const closingTo = closingRowTotal(finalBlocks);
  const wrapParts: string[] = [];
  if (quizTo < quizFrom) wrapParts.push(`개념확인 퀴즈 ${quizFrom}→${quizTo}문항`);
  if (closingTo < closingFrom) {
    const label = finalBlocks.some((b) => b.stage === '정리' && b.type === 'selfAssess') ? '자기평가' : '점검표';
    wrapParts.push(`${label} ${closingFrom}→${closingTo}줄`);
  }
  if (wrapParts.length) notes.push(`2쪽에 맞추려고 정리를 줄였습니다(${wrapParts.join(', ')}).`);
  if (usedLevel >= 6) notes.push('2쪽에 맞추려고 배움 활동의 답칸·그리기 칸과 간격을 더 촘촘하게 했습니다(배움 활동은 모두 그대로).');
  else if (usedLevel >= 5) notes.push('2쪽에 맞추려고 배움 활동의 답칸·그리기 칸을 더 작게 했습니다.');
  const readingLimit = usedLevel >= 6 ? READING_TIGHT_CHARS : usedLevel >= 5 ? READING_DEEP_CHARS : READING_COMPACT_CHARS;
  if (usedLevel >= 4 && finalBlocks.some((b) => b.type === 'reading' && b.text.trim().length > readingLimit)) {
    notes.push('2쪽에 맞추려고 읽기자료를 조금 줄였습니다.');
  }
  if (dropped.length) notes.push(`2쪽에 맞추려고 덧붙인 자료를 뺐습니다: ${dropped.map((t) => `'${t}'`).join(', ')}.`);
  if (droppedLearning.length) {
    notes.push(`2쪽을 넘지 않도록 배움 활동 ${droppedLearning.map((t) => `'${t}'`).join(', ')}을(를) 뺐습니다. 편집기에서 확인해 주세요.`);
  }
  warnings.unshift(...notes);
  const outs: SheetElements[] = Array.from({ length: pageCount }, () => ({ boxes: [], shapes: [], tables: [], webpages: [] }));
  const zCounters = Array.from({ length: pageCount }, () => 1);
  if (title) {
    outs[0].boxes.push(
      textEl(SHEET_W * MARGIN_X, topPx, CONTENT_W_PX, titleH, title, WORKSHEET_TITLE_PX, {
        bold: true,
        align: 'center',
        color: THEME_HEX,
        groupId: 'ws-title',
        z: zCounters[0]++,
      })
    );
  }
  for (const row of placed) {
    const out = outs[row.page];
    const z = () => zCounters[row.page]++;
    if (row.header) row.header.draw(out, SHEET_W * MARGIN_X, row.y, CONTENT_W_PX, row.h, z);
    for (const cell of row.cells) cell.compiled.draw(out, cell.x, row.y, cell.w, row.h, z);
  }

  const pages: ProgramSlideLayoutSubsetV1[] = outs.map((out) => ({
    ...createEmptySheetLayoutSubset(),
    boxes: out.boxes,
    shapes: out.shapes,
    tables: out.tables,
    webpages: out.webpages,
  }));
  const document = normalizeLearningContentWorksheetDocument({
    ...base,
    title: LEARNING_CONTENT_WORKSHEET_TITLE,
    pages,
    layout: pages[0],
    aiSource: {
      v: 1,
      blocks: finalBlocks.map((b) => ({ ...b })),
      // 저장된 활동지를 나중에 다시 맞출 때(쪽 수 제한 등) 쓰는 제목·단계 소제목
      title,
      stageTitles: Object.fromEntries(
        STAGE_ORDER.filter((stage) => (stageTitles[stage] ?? '').trim()).map((stage) => [stage, (stageTitles[stage] ?? '').trim()])
      ),
    },
  });
  return { document, pageCount, warnings };
}

export type WorksheetPageFitResult = {
  document: LearningContentWorksheetDocumentV1;
  /** true면 새로 맞춘 문서(저장 전까지 원래 데이터는 그대로) */
  changed: boolean;
  /** 교사에게 보일 안내. 2쪽 이하면 빈 배열 */
  warnings: string[];
};

type AnyEl = { groupId?: string; text?: string; cells?: Array<{ text?: string }> };

function pageElements(page: ProgramSlideLayoutSubsetV1): AnyEl[] {
  return [
    ...(page.boxes ?? []),
    ...(page.shapes ?? []),
    ...(page.tables ?? []),
    ...(page.webpages ?? []),
    ...(page.polylines ?? []),
    ...(page.images ?? []),
    ...(page.clipboardImages ?? []),
    ...(page.scanImages ?? []),
  ] as AnyEl[];
}

function elementTexts(pages: ProgramSlideLayoutSubsetV1[]): string[] {
  const out: string[] = [];
  for (const page of pages) {
    for (const el of pageElements(page)) {
      if (typeof el.text === 'string' && el.text.trim()) out.push(el.text.trim());
      for (const cell of el.cells ?? []) if (typeof cell.text === 'string' && cell.text.trim()) out.push(cell.text.trim());
    }
  }
  return out;
}

/**
 * 저장된 활동지가 2쪽을 넘으면(예전 3쪽 활동지) 같은 줄이기 순서로 2쪽에 다시 맞춘다.
 * 손대지 않은 AI 활동지(aiSource가 있고 모든 요소·글이 AI 블록과 이어짐)만 맞춘다. 원래 데이터는 바꾸지 않고 새 문서를 돌려준다.
 * 직접 고친 활동지·예전 형식(aiSource 없음)은 그대로 두고 안내만 돌려준다.
 */
export function fitWorksheetDocumentToMaxPages(doc: LearningContentWorksheetDocumentV1): WorksheetPageFitResult {
  const pages = doc.pages ?? [];
  if (pages.length <= MAX_WORKSHEET_PAGES) return { document: doc, changed: false, warnings: [] };
  const manualNote = `이 활동지는 ${pages.length}쪽입니다. 활동지는 2쪽까지라 3쪽부터는 지우거나 내용을 줄여 주세요.`;
  const keep: WorksheetPageFitResult = { document: doc, changed: false, warnings: [manualNote] };
  const source = doc.aiSource;
  if (!source) return keep;
  const blocks = source.blocks.map(coerceAiWorksheetBlock).filter((b): b is AiWorksheetBlock => b != null);
  if (!blocks.length) return keep;
  blocks.forEach((b, i) => {
    if (!b.id) b.id = `b${i + 1}`;
  });
  const meta = source as { title?: unknown; stageTitles?: unknown };
  // 제목·단계 소제목: 새 데이터는 aiSource에, 예전 데이터는 캔버스 글상자에서 읽는다
  const allEls = pages.flatMap(pageElements);
  const titleFromCanvas = allEls.find((el) => el.groupId === 'ws-title' && typeof el.text === 'string')?.text?.trim() ?? '';
  const title = typeof meta.title === 'string' ? meta.title.trim() : titleFromCanvas;
  const stageTitles: AiWorksheetV2['design']['stageTitles'] =
    meta.stageTitles && typeof meta.stageTitles === 'object' ? coerceAiWorksheetStageTitles(meta.stageTitles) : {};
  if (!(meta.stageTitles && typeof meta.stageTitles === 'object')) {
    STAGE_ORDER.forEach((stage, i) => {
      const subtitle = pages
        .flatMap((p) => p.boxes ?? [])
        .find((b) => b.groupId === `ws-stage-${i + 1}` && typeof b.text === 'string' && b.text.trim());
      if (subtitle) stageTitles[stage] = subtitle.text.trim();
    });
  }
  const worksheet: AiWorksheetV2 = { schemaVersion: 2, design: { pages: 2, flow: '', stageTitles }, title, blocks };

  // 손댄 흔적 확인: AI 블록과 이어지지 않은 요소가 있거나, AI가 만든 글이 아닌 글이 있으면 자동으로 맞추지 않는다
  const allowed = new Set<string>(['ws-title', ...STAGE_ORDER.map((_, i) => `ws-stage-${i + 1}`), ...blocks.map((b) => b.id)]);
  if (allEls.some((el) => !el.groupId || !allowed.has(el.groupId))) {
    return { ...keep, warnings: [`이 활동지는 ${pages.length}쪽입니다. 직접 더하거나 고친 내용이 있어 자동으로 2쪽에 맞추지 않았습니다. 3쪽부터는 지우거나 줄여 주세요.`] };
  }
  const reference = buildWorksheetDocumentFromAiV2(worksheet, { reference: true }).document;
  const refTexts = elementTexts(reference.pages);
  const refSet = new Set(refTexts);
  const edited = elementTexts(pages).some((t) => {
    if (refSet.has(t)) return false;
    // 촘촘하게 맞추며 줄인 읽기자료(… 로 끝남)는 원문 앞부분이면 같은 것으로 본다
    const head = t.replace(/\s*…$/, '').trim();
    return !(head.length >= 20 && t.endsWith('…') && refTexts.some((r) => r.startsWith(head)));
  });
  if (edited) {
    return { ...keep, warnings: [`이 활동지는 ${pages.length}쪽입니다. 직접 고친 내용이 있어 자동으로 2쪽에 맞추지 않았습니다. 3쪽부터는 지우거나 줄여 주세요.`] };
  }
  const built = buildWorksheetDocumentFromAiV2(worksheet);
  const document = normalizeLearningContentWorksheetDocument({
    ...built.document,
    title: doc.title || built.document.title,
    academicYearLabel: doc.academicYearLabel,
  });
  return {
    document,
    changed: true,
    warnings: [`${pages.length}쪽이던 활동지를 2쪽에 맞췄습니다(저장하면 반영됩니다).`, ...built.warnings],
  };
}

function emptyBlock(id: string, type: AiWorksheetBlock['type'], stage: AiWorksheetBlock['stage']): AiWorksheetBlock {
  return {
    id,
    type,
    variant: '',
    stage,
    title: '',
    size: 'M',
    span: 2,
    optional: false,
    text: '',
    items: [],
    pairs: [],
    categories: [],
    rows: [],
    lines: 0,
    html: '',
    wordBank: [],
    formative: false,
  };
}

/** 예전 응답(pages→tasks)도 같은 배치기로: 쪽 안내는 읽기자료 1개, 과제는 긴 글 블록. 최대 2쪽, '(이어서)' 없음 */
export function worksheetV2FromLegacyPages(pages: AiLearningWorksheetPageDraft[]): AiWorksheetV2 {
  const blocks: AiWorksheetBlock[] = [];
  const stages: AiWorksheetBlock['stage'][] = ['도입', '배움1', '배움2', '정리'];
  const stageTitles: AiWorksheetV2['design']['stageTitles'] = {};
  pages.forEach((page, pi) => {
    const stage = stages[Math.min(stages.length - 1, pi)];
    if (!stageTitles[stage] && page.title.trim()) stageTitles[stage] = page.title.trim().slice(0, 30);
    const lead = page.lead.trim();
    if (lead && !blocks.some((b) => b.type === 'reading')) {
      blocks.push({ ...emptyBlock(`b${blocks.length + 1}`, 'reading', stage), title: page.title.trim() || '읽어 보세요', size: 'S', text: lead });
    }
    for (const task of page.tasks) {
      const prompt = task.prompt.trim();
      if (!prompt) continue;
      blocks.push({
        ...emptyBlock(`b${blocks.length + 1}`, 'long', stage),
        title: prompt,
        lines: Math.min(8, Math.max(2, Math.round(task.writeLines || 3))),
      });
    }
  });
  return { schemaVersion: 2, design: { pages: 2, flow: '', stageTitles }, title: '', blocks };
}

/** 예전 응답(pages)으로 활동지 문서(같은 배치기, 최대 2쪽) */
export function buildWorksheetDocumentFromAi(pages: AiLearningWorksheetPageDraft[]): LearningContentWorksheetDocumentV1 {
  return buildWorksheetDocumentFromAiV2(worksheetV2FromLegacyPages(pages)).document;
}
